import {Trans} from "@lingui/react/macro";
import {useLayoutEffect, useRef, useState} from "react";
import type {NavigateFunction, RouteObject} from "react-router";
import {useNavigate, useParams} from "react-router";
import AddIngredient from "../../features/recipes/AddIngredient";
import EditIngredient from "../../features/recipes/EditIngredient";
import {EditIngredientPill} from "../../features/recipes/IngredientPill";
import {RecipeForm} from "../../features/recipes/RecipeForms";
import type {RecipeFormValues} from "../../features/recipes/RecipeForms";
import UploadImage from "../../features/recipes/UploadImage";
import type {
  AssetId,
  GroupRepo,
  IngredientEntry,
  ItemEntry,
  ItemId,
  RecipeEntry,
  RecipeId,
} from "../../models/GroupRepo";
import {getIngredientIdsForRecipe, useSortedIngredients} from "../../models/recipe-queries";
import type {UserRepo} from "../../models/UserRepo";
import AppShell from "../../shared/ui/AppShell";
import GroupFooterNav from "../../shared/ui/GroupFooterNav";
import Header from "../../shared/ui/Header";
import {Col, css, Row, Text} from "../../ui/Box";
import Button, {FloatingButton} from "../../ui/Button";
import {ControlledDrawer, DrawerRoutes, RouteDrawer} from "../../ui/Drawer";
import Icon from "../../ui/Icon";
import {findNearestIndexViaBinarySearch} from "../../utils";
import {findClosestScrollableParent} from "../../utils/dom-utils";
import {useDndList} from "../../utils/list-dnd/use-dnd-list";
import {ShowAsset} from "../../utils/ShowAsset";
import {createSorter, moveAndEnsureBalance} from "../../utils/sort-utils";

const getModalRoutes = (
  groupRepo: GroupRepo,
  recipe: RecipeEntry,
  userRepo: UserRepo
): RouteObject[] => [
  {
    path: "upload",
    element: (
      <RouteDrawer backPath=".." title="Upload Image" asModal>
        <UploadImage groupRepo={groupRepo} userRepo={userRepo} recipe={recipe} />
      </RouteDrawer>
    ),
  },
  {
    path: "add-ingredient",
    element: (
      <RouteDrawer backPath=".." title="Add Ingredient">
        <AddIngredient groupRepo={groupRepo} recipe={recipe} />
      </RouteDrawer>
    ),
  },
  {
    path: "ingredient/:ingredientId",
    element: (
      <RouteDrawer backPath=".." title="Edit Ingredient">
        <EditIngredient groupRepo={groupRepo} />
      </RouteDrawer>
    ),
  },
];

const LIST_GAP = 4;

const IngredientList = ({
  ingredients,
  groupRepo,
  navigate,
  items,
}: {
  ingredients: IngredientEntry[];
  groupRepo: GroupRepo;
  navigate: NavigateFunction;
  items: Record<ItemId, ItemEntry>;
}) => {
  const nodeRef = useRef<HTMLElement | null>(null);
  const scrollNodeRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    // This approach was chosen over passing a ref from the parent as
    // useVirtual uses useLayoutEffect, and children's useLayoutEffect are
    // called _before_ the parent gets a chance to set refs (both via useRef
    // and callback refs)
    scrollNodeRef.current = findClosestScrollableParent(nodeRef.current!) as HTMLElement;
  }, []);

  const {height, virtualItems, scrollMargin} = useDndList({
    listContainerRef: nodeRef,
    count: ingredients.length,
    getScrollElement: () => scrollNodeRef.current,
    gap: LIST_GAP,
    height: 48,
    onDrop: ({targetIndex, startIndex}) => {
      if (targetIndex === startIndex) return;
      let before;
      let after;
      if (startIndex > targetIndex) {
        before = targetIndex > 0 ? ingredients[targetIndex - 1] : null;
        after = ingredients[targetIndex];
      } else {
        before = ingredients[targetIndex];
        after = targetIndex < ingredients.length - 1 ? ingredients[targetIndex + 1] : null;
      }
      const updateEls = moveAndEnsureBalance({
        targetEl: ingredients[startIndex],
        prevEl: before,
        nextEl: after,
        getSortIndex: (el) => el.sortIndex,
        sorter: createSorter(),
        getSortedElementsBetween: (start, end) => {
          const [startIdx] = findNearestIndexViaBinarySearch(
            ingredients,
            start,
            (el) => el.sortIndex
          );
          const [, endIdx] = findNearestIndexViaBinarySearch(
            ingredients,
            end,
            (el) => el.sortIndex
          );
          const els = ingredients.slice(
            Math.max(0, startIdx),
            Math.min(ingredients.length - 1, endIdx) + 1
          );
          return els;
        },
      });
      groupRepo.store.transaction(() => {
        updateEls.forEach(([el, sortIndex]) => {
          groupRepo.Ingredients.patch(el.id, {
            sortIndex,
          });
        });
      });
    },
  });

  return (
    <Col relative style={{height}} ref={nodeRef}>
      {virtualItems.map((item) => {
        const ingredient = ingredients[item.index];
        return (
          <EditIngredientPill
            key={ingredient.id}
            item={items[ingredient.itemId]}
            ingredient={ingredient}
            navigate={navigate}
            index={item.index}
            startPx={item.start - scrollMargin}
            groupRepo={groupRepo}
          />
        );
      })}
    </Col>
  );
};

const PreviewImage = ({assetId, groupRepo}: {assetId: AssetId; groupRepo: GroupRepo}) => {
  const groupId = groupRepo.vals.useValues().id;
  const asset = groupRepo.Assets.useById(assetId);
  if (!asset) return null;
  return <ShowAsset asset={asset} groupId={groupId} className={css({height: "auto"})} />;
};

const CoverImage = ({recipe, groupRepo}: {recipe: RecipeEntry; groupRepo: GroupRepo}) => {
  return (
    <Col rounded="4" borderWidth={1} borderColor="default" pa="16" sp="12">
      {recipe.coverImage && <PreviewImage assetId={recipe.coverImage} groupRepo={groupRepo} />}
      <Button to="upload">Upload Image</Button>
    </Col>
  );
};

const InnerEdit = ({
  recipe,
  groupRepo,
  userRepo,
}: {
  groupRepo: GroupRepo;
  userRepo: UserRepo;
  recipe: RecipeEntry;
}) => {
  const navigate = useNavigate();
  const ingredients = useSortedIngredients(groupRepo, recipe.id);
  const items = groupRepo.Items.useGetAll();
  const handleSubmit = ({recipeName, description, defaultPortions}: RecipeFormValues) => {
    groupRepo.Recipes.patch(recipe.id, {
      name: recipeName,
      description,
      defaultPortions,
    });
  };
  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="floatingButton">
      <h1>Edit Recipe and Ingredients</h1>
      <RecipeForm
        onSubmit={handleSubmit}
        initial={{
          recipeName: recipe.name,
          description: recipe.description ?? "",
          defaultPortions: `${recipe.defaultPortions}`,
        }}
      />
      <CoverImage recipe={recipe} groupRepo={groupRepo} />
      <IngredientList
        ingredients={ingredients}
        groupRepo={groupRepo}
        navigate={navigate}
        items={items}
      />
      <FloatingButton to="add-ingredient">
        <Icon name="plus" />
      </FloatingButton>
      <DrawerRoutes routes={getModalRoutes(groupRepo, recipe, userRepo)} key={recipe.id} />
    </Col>
  );
};

type DeleteModalContentProps = {
  groupRepo: GroupRepo;
  recipe: RecipeEntry;
  onClose: () => void;
};
const DeleteModalContent = ({groupRepo, recipe, onClose}: DeleteModalContentProps) => {
  const navigate = useNavigate();
  const handleDelete = () => {
    const ingIds = getIngredientIdsForRecipe(groupRepo, recipe.id);
    groupRepo.store.transaction(() => {
      for (const scheduled of Object.values(groupRepo.ScheduledRecipes.getAll())) {
        if (scheduled.recipeId === recipe.id) {
          groupRepo.ScheduledRecipes.delete(scheduled.id);
        }
      }
      for (const id of ingIds) {
        groupRepo.Ingredients.delete(id);
      }
      groupRepo.Recipes.delete(recipe.id);
    });
    navigate("..");
  };
  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
      <Text size="14_tight">Are you sure!?</Text>
      <Row sp="4">
        <Button onClick={onClose}>no</Button>
        <Button onClick={handleDelete}>yes</Button>
      </Row>
    </Col>
  );
};

const DeleteButton = ({groupRepo, recipe}: {groupRepo: GroupRepo; recipe: RecipeEntry}) => {
  const [showWarning, setShowWarning] = useState(false);
  const off = () => setShowWarning(false);
  const on = () => setShowWarning(true);
  return (
    <>
      <ControlledDrawer open={showWarning} onClose={off} title="Delete List">
        <DeleteModalContent groupRepo={groupRepo} recipe={recipe} onClose={off} />
      </ControlledDrawer>
      <Button variant="tertiary" onClick={on}>
        <Icon name="trash" />
      </Button>
    </>
  );
};

const EditRecipe = ({groupRepo, userRepo}: {groupRepo: GroupRepo; userRepo: UserRepo}) => {
  const params = useParams<{recipeId: RecipeId}>();
  const group = groupRepo.vals.useValues();
  const recipe = groupRepo.Recipes.useById(params.recipeId!);
  return (
    <AppShell
      header={
        <Header
          title={recipe ? recipe.name : "Error"}
          backButton={{to: "..", label: <Trans id="nav.back">Back</Trans>}}
          actions={recipe && <DeleteButton groupRepo={groupRepo} recipe={recipe} />}
        />
      }
      footer={<GroupFooterNav current="cook" group={group} />}
    >
      <Col px="12" py="8" sp="24">
        {recipe ? (
          <InnerEdit recipe={recipe} groupRepo={groupRepo} userRepo={userRepo} />
        ) : (
          <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" pb="24">
            <Text size="12" color="secondary">
              No such recipe
            </Text>
          </Col>
        )}
      </Col>
    </AppShell>
  );
};

export default EditRecipe;
